import { useEffect, useMemo, useRef, useState } from "react";
import { fmtTime } from "@/lib/format";
import { postChat, useChat } from "@/lib/store";
import type { ChatMessage, ChatPerson } from "@/lib/views";

/**
 * 桌邊聊天室。
 *
 * 存取控制沿用登機證：有這場活動的票才看得到、發得了。
 * 聊天內容含真名與座位，不該讓知道活動代號的路人看見。
 *
 * 每則訊息標示發話者的桌號，所以「找 3 桌的人講話」是成立的；
 * 想專心跟同桌聊時可以切「只看本桌」。
 *
 * 可以 @ 別人。tag 不會把對話變成私訊 —— 訊息仍然全場可見，
 * 被 tag 的人只是會在自己的聊天室裡看到「有人在找我」。
 * 這是刻意的：這場活動只有幾個小時，價值在於把不認識的人拉進同一個話題，
 * 私訊會把對話從公開場域抽走。
 */
export function Chat({ token }: { token: string }) {
  const view = useChat(token);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [onlyMyTable, setOnlyMyTable] = useState(false);
  const [onlyMentions, setOnlyMentions] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const messages = useMemo(() => view?.messages ?? [], [view]);
  const myTable = view?.myTable;
  const people = view?.people ?? [];
  const taggedCount = messages.filter((m) => m.mentionsMe).length;

  const shown = onlyMentions
    ? messages.filter((m) => m.mentionsMe)
    : onlyMyTable && myTable
      ? messages.filter((m) => m.table === myTable)
      : messages;

  // 有新訊息時捲到底。只在「本來就在底部附近」時才捲 ——
  // 使用者正在往上翻舊訊息時把他拉回底部很煩。
  const lastId = shown.at(-1)?.id;
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
    if (nearBottom) el.scrollTop = el.scrollHeight;
  }, [lastId]);

  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    const body = draft.trim();
    if (!body || busy) return;
    setBusy(true);
    setError(null);
    const res = await postChat(token, body);
    setBusy(false);
    if (!res.ok) {
      setError(res.reason === "too-fast" ? "說慢一點～稍等一下再送出" : "送出失敗，請再試一次");
      return;
    }
    setDraft("");
    // 送出後一定捲到底：那是自己剛講的話
    requestAnimationFrame(() => {
      if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
    });
  };

  if (view === undefined) {
    return <div className="text-sub card px-5 py-6 text-center text-[13px]">載入聊天室…</div>;
  }
  if (view === null) return null;

  return (
    <div className="card overflow-hidden">
      <div className="border-line flex flex-wrap items-center justify-between gap-2 border-b px-5 py-3.5">
        <div>
          <h2 className="text-[15px] font-bold">桌邊聊天</h2>
          <p className="text-sub text-[12px]">
            {myTable ? `你在第 ${myTable} 桌` : "你還沒有座位"} · 只有本場的人看得到
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {taggedCount > 0 && (
            <label className="text-warn-deep flex items-center gap-1.5 text-[13px] font-medium">
              <input
                type="checkbox"
                checked={onlyMentions}
                onChange={(e) => setOnlyMentions(e.target.checked)}
                className="h-4 w-4 accent-[#a35f00]"
              />
              有人找你（{taggedCount}）
            </label>
          )}
          {myTable && (
            <label className="flex items-center gap-1.5 text-[13px]">
              <input
                type="checkbox"
                checked={onlyMyTable}
                disabled={onlyMentions}
                onChange={(e) => setOnlyMyTable(e.target.checked)}
                className="h-4 w-4 accent-[#007aff]"
              />
              只看本桌
            </label>
          )}
        </div>
      </div>

      <div ref={listRef} className="flex max-h-[420px] flex-col gap-2.5 overflow-y-auto px-4 py-4">
        {shown.length === 0 ? (
          <p className="text-sub py-8 text-center text-[13px]">
            {onlyMentions
              ? "還沒有人 @ 你"
              : onlyMyTable
                ? "本桌還沒有人說話"
                : "還沒有人說話——說第一句吧"}
          </p>
        ) : (
          shown.map((m) => <Bubble key={m.id} m={m} />)
        )}
      </div>

      <form onSubmit={send} className="border-line border-t px-4 py-3">
        <Composer
          value={draft}
          onChange={setDraft}
          people={people}
          inputRef={inputRef}
          busy={busy}
        />
      </form>
      {error && <p className="text-bad-deep px-4 pb-3 text-[12px]">{error}</p>}
    </div>
  );
}

/** 輸入框打 @ 之後，游標前面那一段還沒打完的名字 */
function mentionQuery(text: string): string | null {
  const at = text.lastIndexOf("@");
  if (at < 0) return null;
  const after = text.slice(at + 1);
  // 打完一個名字之後就不該再跳選單了：有空白或換行代表這個 @ 已經結束
  if (/[\s\n]/.test(after)) return null;
  // 名字最長 30 字（跟報名時的上限一致），超過就不是在打名字了
  return after.length <= 30 ? after : null;
}

/**
 * 訊息輸入框 + @ 選單。
 *
 * 只有名單上的名字 tag 得到（後端拿名單比對訊息內容），所以這裡讓人直接挑，
 * 而不是自己打完再祈禱拼對 —— 這跟報名頁的接龍名單選單是同一個道理。
 */
function Composer({
  value,
  onChange,
  people,
  inputRef,
  busy,
}: {
  value: string;
  onChange: (v: string) => void;
  people: ChatPerson[];
  inputRef: React.RefObject<HTMLInputElement | null>;
  busy: boolean;
}) {
  const [active, setActive] = useState(0);
  const [dismissed, setDismissed] = useState(false);

  const query = mentionQuery(value);
  const matches = useMemo(() => {
    if (query === null) return [];
    const q = query.toLowerCase();
    return people.filter((p) => p.name.toLowerCase().includes(q)).slice(0, 6);
  }, [query, people]);
  const open = !dismissed && matches.length > 0;

  const pick = (name: string) => {
    const at = value.lastIndexOf("@");
    // 補一個空白：名字後面接著就打字的話，下一個字會被算進名字裡
    onChange(value.slice(0, at) + "@" + name + " ");
    setActive(0);
    setDismissed(true);
    inputRef.current?.focus();
  };

  return (
    <div className="relative">
      <div className="flex items-center gap-2">
        <input
          ref={inputRef}
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            setActive(0);
            setDismissed(false);
          }}
          onKeyDown={(e) => {
            if (!open) return;
            if (e.key === "ArrowDown") {
              setActive((i) => (i + 1) % matches.length);
              e.preventDefault();
            } else if (e.key === "ArrowUp") {
              setActive((i) => (i - 1 + matches.length) % matches.length);
              e.preventDefault();
            } else if (e.key === "Enter" || e.key === "Tab") {
              // 選單開著時的 Enter 是「選這個人」，不是「送出訊息」
              e.preventDefault();
              pick(matches[active].name);
            } else if (e.key === "Escape") {
              setDismissed(true);
            }
          }}
          maxLength={200}
          placeholder="跟大家說點什麼…（打 @ 可以叫人）"
          className="field-input flex-1 text-[14px]"
          aria-label="輸入訊息"
          autoComplete="off"
        />
        <button
          type="submit"
          disabled={busy || !value.trim()}
          className="btn btn-primary px-4 py-2.5 text-[14px]"
        >
          送出
        </button>
      </div>

      {open && (
        <ul
          role="listbox"
          className="border-line bg-card absolute bottom-full z-20 mb-1.5 max-h-56 w-full max-w-xs overflow-y-auto rounded-xl border py-1 shadow-lg"
          // mousedown 會先於 blur 發生，不擋的話點下去輸入框就失焦了
          onMouseDown={(e) => e.preventDefault()}
        >
          {matches.map((p, i) => (
            <li key={`${p.name}-${p.table ?? ""}`} role="option" aria-selected={i === active}>
              <button
                type="button"
                onClick={() => pick(p.name)}
                onMouseEnter={() => setActive(i)}
                className={`flex w-full items-center justify-between gap-3 px-3.5 py-2 text-left text-[14px] ${
                  i === active ? "bg-inset" : ""
                }`}
              >
                <span className="font-medium">{p.name}</span>
                {p.table && <span className="text-sub shrink-0 text-[12px]">{p.table} 桌</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * 把訊息裡被 tag 的名字標出來。
 *
 * 標的依據是 server 回傳的 mentions（送出當下就定案的那份），
 * 而不是在前端拿正規表示式找 @ 開頭的字 —— 中文名字沒有結束邊界，
 * 前端猜出來的範圍會跟後端實際 tag 到的人不一致。
 */
function withMentions(body: string, mentions: string[] | undefined, mine: boolean) {
  if (!mentions?.length) return body;
  // 長名字優先，理由跟後端一樣：短名字不能在長名字裡面又中一次
  const sorted = [...mentions].sort((a, b) => b.length - a.length);
  const parts: React.ReactNode[] = [];
  let rest = body;
  let key = 0;

  while (rest) {
    let at = -1;
    let hit = "";
    for (const name of sorted) {
      const i = rest.indexOf("@" + name);
      if (i >= 0 && (at < 0 || i < at)) {
        at = i;
        hit = name;
      }
    }
    if (at < 0) {
      parts.push(rest);
      break;
    }
    if (at > 0) parts.push(rest.slice(0, at));
    parts.push(
      <span key={key++} className={`font-semibold ${mine ? "underline" : "text-accent"}`}>
        @{hit}
      </span>,
    );
    rest = rest.slice(at + hit.length + 1);
  }
  return parts;
}

function Bubble({ m }: { m: ChatMessage }) {
  return (
    <div className={`flex flex-col ${m.mine ? "items-end" : "items-start"}`}>
      <div className="text-sub mb-0.5 px-1 text-[11px]">
        {m.table && <span className="text-accent font-semibold">{m.table} 桌</span>}
        {m.table && " · "}
        {m.name} · {fmtTime(m.createdAt)}
        {m.mentionsMe && <span className="text-warn-deep ml-1.5 font-semibold">找你</span>}
      </div>
      <div
        className={`max-w-[85%] whitespace-pre-wrap break-words rounded-2xl px-3.5 py-2 text-[14px] leading-relaxed ${
          m.mine
            ? "bg-accent text-white"
            : m.mentionsMe
              ? "bg-warn/10 ring-warn/40 ring-1"
              : "bg-inset"
        }`}
      >
        {withMentions(m.body, m.mentions, !!m.mine)}
      </div>
    </div>
  );
}
